import { ref, computed, nextTick, onBeforeUnmount, watch, type Ref } from 'vue'

export type FloatingLayerDirection = 'up' | 'down'
export type FloatingStrategy = 'fixed' | 'absolute'

export type FloatingOptions = {
  /** Sélecteur du conteneur bornant la zone d’affichage. défaut: viewport */
  containerSelector?: string
  /** Bords internes pour éviter de coller aux bords du container */
  edgeX?: number
  innerMargin?: number
  gapY?: number
  maxMenuHeight?: number
  itemHeightEstimate?: number
  /** Stratégie de positionnement. `fixed` recommandé si Teleport vers body */
  strategy?: FloatingStrategy
  /** Fermer si le trigger sort trop de la vue */
  closeTolerance?: number
  /** Fermer sur Escape */
  closeOnEsc?: boolean
}

export function useFloatingLayer(
  isOpen: Ref<boolean>,
  triggerRef: Ref<HTMLElement | null>,
  layerRef: Ref<HTMLElement | null>,
  options: FloatingOptions = {},
) {
  const cfg = {
    containerSelector: options.containerSelector ?? '.auth-content',
    edgeX: options.edgeX ?? 8,
    innerMargin: options.innerMargin ?? 12,
    gapY: options.gapY ?? 6,
    maxMenuHeight: options.maxMenuHeight ?? 300,
    itemHeightEstimate: options.itemHeightEstimate ?? 40,
    closeTolerance: options.closeTolerance ?? 40,
    strategy: options.strategy ?? 'fixed',
    closeOnEsc: options.closeOnEsc ?? true,
  } as const

  const direction = ref<FloatingLayerDirection>('down')
  const overlayStyle = ref<Record<string, string>>({})

  const viewportRect = () => ({
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const closest = (el: HTMLElement | null, sel: string) =>
    (el?.closest?.(sel) as HTMLElement | null) ?? (document.querySelector(sel) as HTMLElement | null)

  const getScrollParents = (el: HTMLElement | null): (HTMLElement | Window)[] => {
    if (!el) return [window]
    const res: (HTMLElement | Window)[] = []
    let n: HTMLElement | null = el.parentElement
    const rx = /(auto|scroll|overlay)/
    while (n && n !== document.body) {
      const cs = getComputedStyle(n)
      if (rx.test(cs.overflow + cs.overflowY + cs.overflowX)) res.push(n)
      n = n.parentElement
    }
    res.push(window)
    return res
  }

  const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

  const estimateContentHeight = () => {
    const layer = layerRef.value
    if (!layer) return cfg.itemHeightEstimate
    // si contenu déjà rendu : on se base sur scrollHeight
    const h = Math.max(layer.scrollHeight, layer.clientHeight)
    return h > 0 ? h : cfg.itemHeightEstimate
  }

  const updatePosition = () => {
    const trigger = triggerRef.value
    const layer = layerRef.value
    if (!trigger || !layer) return

    const rect = trigger.getBoundingClientRect()
    const container = closest(trigger, cfg.containerSelector)
    const cRect = container?.getBoundingClientRect() ?? viewportRect()

    const leftClamp = Math.max(rect.left, cRect.left + cfg.edgeX)
    const rightClamp = Math.min(cRect.right - cfg.edgeX, rect.left + rect.width)
    const width = Math.max(0, rightClamp - leftClamp) || rect.width

    const above = Math.max(0, rect.top - cRect.top)
    const below = Math.max(0, cRect.bottom - rect.bottom)
    const estHeight = estimateContentHeight()
    const showUp = estHeight > below && above > below
    direction.value = showUp ? 'up' : 'down'

    const avail = Math.max(0, (showUp ? rect.top - cRect.top : cRect.bottom - rect.bottom) - cfg.innerMargin)
    const clampedMax = Math.min(cfg.maxMenuHeight, avail)
    const desiredHeight = Math.min(estimateContentHeight(), clampedMax)

    const left = clamp(Math.max(leftClamp, rect.left), leftClamp, cRect.right - cfg.edgeX - width)
    const rawTop = showUp ? rect.top - desiredHeight - cfg.gapY : rect.bottom + cfg.gapY
    const top = clamp(rawTop, cRect.top + cfg.edgeX, cRect.bottom - cfg.edgeX - desiredHeight)

    overlayStyle.value = {
      position: cfg.strategy,
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      maxHeight: `${clampedMax}px`,
      overflowY: 'auto',
      willChange: 'top, left, width, height',
      zIndex: '999',
    }

    layer.style.maxHeight = `${clampedMax}px`
    layer.style.overflowY = 'auto'
  }

  const checkTriggerVisibility = () => {
    if (!isOpen.value || !triggerRef.value) return
    const trigger = triggerRef.value
    const container = closest(trigger, cfg.containerSelector)
    const cRect = container?.getBoundingClientRect() ?? viewportRect()
    const tRect = trigger.getBoundingClientRect()
    if (tRect.bottom < cRect.top + cfg.closeTolerance || tRect.top > cRect.bottom - cfg.closeTolerance) {
      isOpen.value = false
    }
  }

  // rAF coalescing
  let rafId: number | null = null
  const schedule = () => {
    if (rafId != null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      updatePosition()
    })
  }

  // listeners
  let scrollSources: (HTMLElement | Window)[] = []
  const onChange = () => {
    if (!isOpen.value) return
    schedule()
    checkTriggerVisibility()
  }
  const attach = () => {
    detach()
    scrollSources = getScrollParents(triggerRef.value)
    scrollSources.forEach((s) => (s as Window | HTMLElement).addEventListener('scroll', onChange, { passive: true }))
    window.addEventListener('resize', onChange, { passive: true })
    if (cfg.closeOnEsc) window.addEventListener('keydown', onKeydown)
  }
  const detach = () => {
    scrollSources.forEach((s) => (s as Window | HTMLElement).removeEventListener('scroll', onChange))
    scrollSources = []
    window.removeEventListener('resize', onChange)
    if (cfg.closeOnEsc) window.removeEventListener('keydown', onKeydown)
  }

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') isOpen.value = false
  }

  const open = async () => {
    if (isOpen.value) return
    isOpen.value = true
    await nextTick()
    updatePosition()
    attach()
  }
  const close = () => {
    if (!isOpen.value) return
    isOpen.value = false
    detach()
  }
  const toggle = async () => (isOpen.value ? close() : open())

  // ✅ IMPORTANT : réagit aux changements externes du v-model
  watch(isOpen, async (v) => {
    if (v) {
      await nextTick()
      updatePosition()
      attach()
    } else {
      detach()
    }
  })

  onBeforeUnmount(() => {
    if (rafId != null) cancelAnimationFrame(rafId)
    detach()
  })

  return { isOpen, direction: computed(() => direction.value), overlayStyle, open, close, toggle, updatePosition }
}
